import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { LuChevronsUpDown } from 'react-icons/lu';

import { cn } from 'utils/cn';

import { Button } from './button';
import { Checkbox } from './checkbox';
import type { ComboboxOption } from './combobox';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from './command';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from './form';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface MultiComboboxProps {
    options: ComboboxOption[];
    selectOptionLabel?: string;
    noOptionsLabel?: string;
    value?: string[];
    onValueChange?: (value: string[]) => void;
    placeholder?: string;
    className?: string;
    maxVisibleLabels?: number;
    renderSummary?: (labels: string[], values: string[]) => string;
}

interface FormMultiComboboxProps extends Omit<MultiComboboxProps, 'value' | 'onValueChange'> {
    formMethods: UseFormReturn<any, any, any>;
    name: string;
    label?: string;
    description?: string;
    required?: boolean;
    rules?: any;
    onChange?: (value: string[]) => void;
}

export const MultiCombobox = (props: MultiComboboxProps) => {
    const [open, setOpen] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState<string[]>([]);

    const value = props.value !== undefined ? props.value : internalValue;
    const setValue = props.onValueChange || setInternalValue;

    const noOptionsLabel = props.noOptionsLabel || 'No option found';
    const selectOptionLabel = props.selectOptionLabel || 'Select options';
    const placeholder = props.placeholder || selectOptionLabel;

    const toggle = (val: string) => {
        const set = new Set(value);
        if (set.has(val)) set.delete(val);
        else set.add(val);
        setValue(Array.from(set));
    };

    const selectedLabels = props.options.filter((o) => value.includes(o.value)).map((o) => o.label);

    const summary = props.renderSummary
        ? props.renderSummary(selectedLabels, value)
        : (() => {
              const max = props.maxVisibleLabels ?? 2;
              if (value.length === 0) return placeholder;
              const head = selectedLabels.slice(0, max).join(', ');
              const tail = selectedLabels.length > max ? '…' : '';
              return `${value.length} selected${head ? `: ${head}${tail}` : ''}`;
          })();

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn('w-full justify-between', props.className)}
                >
                    <span className="truncate">{summary}</span>
                    <LuChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
                <Command>
                    <CommandInput placeholder={`Search ${placeholder.toLowerCase()}...`} className="h-9" />
                    <CommandList>
                        <CommandEmpty>{noOptionsLabel}</CommandEmpty>
                        {props.options.map((option) => {
                            const checked = value.includes(option.value);
                            return (
                                <CommandItem
                                    key={option.value}
                                    value={option.label}
                                    onSelect={() => toggle(option.value)}
                                    className="group"
                                >
                                    <Checkbox
                                        checked={checked}
                                        className="mr-2 group-data-[selected=true]:ring-1 group-data-[selected=true]:ring-current"
                                    />
                                    {option.label}
                                </CommandItem>
                            );
                        })}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export const FormMultiCombobox = (props: FormMultiComboboxProps) => {
    return (
        <FormField
            control={props.formMethods.control}
            name={props.name}
            rules={props.rules}
            render={({ field }) => {
                const currentValues: string[] = Array.isArray(field.value) ? (field.value as string[]) : [];

                const handleValueChange = (newValues: string[]) => {
                    try {
                        field.onChange(newValues);
                        props.onChange?.(newValues);
                    } catch (e) {
                        // no-op
                    }
                };

                return (
                    <FormItem className="flex flex-col gap-2">
                        {props.label && <FormLabel>{props.label}</FormLabel>}
                        <FormControl>
                            <div className="relative">
                                <MultiCombobox
                                    options={props.options}
                                    value={currentValues}
                                    onValueChange={handleValueChange}
                                    placeholder={props.placeholder}
                                    selectOptionLabel={props.selectOptionLabel}
                                    noOptionsLabel={props.noOptionsLabel}
                                    className={props.className}
                                    maxVisibleLabels={props.maxVisibleLabels}
                                    renderSummary={props.renderSummary}
                                />
                                {/* Hidden input for native HTML validation */}
                                {props.required && (
                                    <input
                                        type="text"
                                        value={currentValues.join(',')}
                                        required={props.required}
                                        onChange={() => {}}
                                        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
                                        tabIndex={-1}
                                        aria-hidden="true"
                                    />
                                )}
                            </div>
                        </FormControl>
                        {props.description && <FormDescription>{props.description}</FormDescription>}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
};

FormMultiCombobox.displayName = 'FormMultiCombobox';
